import { EVENT_SITES } from '@crossbeam/itly';

import { pluralize } from 'humanize-plus';
import { type Ref, computed, reactive, ref, watch } from 'vue';

import { crossbeamApi } from '@/api';
import { sortPopulationsByStandardType } from '@/composables/dataSharingHub/useSharingSettingsHub';
import useDataSharingPermissions from '@/composables/useDataSharingPermissions';
import useDataSharingTracking from '@/composables/useDataSharingTracking';
import { useFieldPresetsModal } from '@/composables/useFieldPresetsModal';
import {
  HIDDEN,
  OLAPS_ONLY,
  PARTNER_RULE,
  POPULATION_RULE,
  SHARING,
  type SharingType,
} from '@/constants/data_shares';
import { isGoogleOrCsvSource } from '@/constants/data_sources';
import {
  useDataSharePresetsStore,
  useDataSharesStore,
  useFlashesStore,
  usePartnersStore,
  usePopulationsStore,
  useSourcesStore,
} from '@/stores';
import type { PresetOptionsGroup } from '@/types/data_share_presets';
import { ALL_SEGMENTS, SEGMENTS_TO_LABEL_MAP } from '@/types/populations';
import type { Population } from '@/types/populations';
import { getFeedIdForPopulation as getFeedIdForPopulationUtil } from '@/utils/getFeedIdForPopulation';

type SharingSetting = {
  visibility: SharingType;
  presetId: number | null;
};

export type DataSourceGroup = {
  feedId: number;
  sourceId: number | undefined;
  populations: Population[];
  presetOptions: PresetOptionsGroup[];
  displayPresetId: number | null;
};

export type PopulationOption = {
  label: string;
  value: number;
  integrationIcon: string | null;
};

export type PopulationOptionGroup = {
  label: string;
  options: PopulationOption[];
};

export type PartnerOption = {
  label: string;
  value: number;
  org: {
    name: string;
    domain?: string | null;
    clearbit_domain?: string | null;
    logo_url?: string | null;
  };
};

type Props = {
  selectedPopulationIds: Ref<number[]>;
  selectedPartnerIds: Ref<number[]>;
  isForPartner?: boolean;
  isNewPopulation?: boolean;
};

export function usePopulationSharingModal(
  props: Props,
  emit: (event: 'close' | 'saved') => void,
) {
  const populationsStore = usePopulationsStore();
  const dataSharesStore = useDataSharesStore();
  const dataSharePresetsStore = useDataSharePresetsStore();
  const sourcesStore = useSourcesStore();
  const partnersStore = usePartnersStore();
  const flashesStore = useFlashesStore();

  const { openModal } = useFieldPresetsModal();
  const { trackDataSharingChangeFromContext } = useDataSharingTracking();

  // Permissions
  // When editing from partner context (isForPartner), only require manage:sharing
  // When editing populations directly, require both manage:populations and manage:sharing
  const { canEdit } = useDataSharingPermissions({
    isPartnerContext: props.isForPartner,
  });

  // Greenfield blocking state from store
  const isGreenfieldSharingBlocked = computed(
    () => dataSharesStore.isGreenfieldSharingBlocked,
  );

  // State
  const populationSettings = ref<Record<number, SharingSetting>>({});
  const partnerSettings = ref<Record<number, SharingSetting>>({});
  const initialSettings = ref<Record<number, SharingSetting>>({});
  const isSaving = ref(false);
  const globalVisibility = ref<SharingType | null>(HIDDEN);
  const initialGlobalVisibility = ref<SharingType | null>(HIDDEN);
  const visiblePopulationCounts = reactive<Record<string, number>>({});

  // Computed - Populations
  const selectedPopulations = computed(() => {
    return props.selectedPopulationIds.value
      .map((id) => populationsStore.getPopulationById(id))
      .filter((pop): pop is Population => pop !== undefined);
  });

  const allPopulationOptions = computed<
    (PopulationOption | PopulationOptionGroup)[]
  >(() => {
    const groups: PopulationOptionGroup[] = [];

    for (const segment of ALL_SEGMENTS) {
      const segmentPops = populationsStore.getPopulationsBySegment(segment);
      if (!segmentPops.length) continue;

      const sorted = [...segmentPops].sort(sortPopulationsByStandardType);
      groups.push({
        label: SEGMENTS_TO_LABEL_MAP[segment],
        options: sorted.map((pop) => ({
          label: pop.name,
          value: pop.id,
          integrationIcon: pop.integration_type ?? null,
        })),
      });
    }

    const unsegmentedPops = populationsStore.populations.filter(
      (pop) => !pop.segment,
    );
    if (unsegmentedPops.length) {
      const toOption = (pop: Population): PopulationOption => ({
        label: pop.name,
        value: pop.id,
        integrationIcon: pop.integration_type ?? null,
      });
      const otherGroup = groups.find(
        (g) => g.label === SEGMENTS_TO_LABEL_MAP.other,
      );
      if (otherGroup) {
        const existingValues = new Set(otherGroup.options.map((o) => o.value));
        const newPops = unsegmentedPops.filter(
          (pop) => !existingValues.has(pop.id),
        );
        const segmentPops = populationsStore.getPopulationsBySegment('other');
        const merged = [...segmentPops, ...newPops].sort(
          sortPopulationsByStandardType,
        );
        otherGroup.options = merged.map(toOption);
      } else {
        const sorted = [...unsegmentedPops].sort(sortPopulationsByStandardType);
        groups.push({
          label: SEGMENTS_TO_LABEL_MAP.other,
          options: sorted.map(toOption),
        });
      }
    }

    return groups;
  });

  const allPartnerOptions = computed<PartnerOption[]>(() =>
    partnersStore.partnerOrgs.map((partner) => ({
      label: partner.name,
      value: partner.id,
      org: {
        name: partner.name,
        domain: partner.domain,
        clearbit_domain: partner.clearbit_domain,
        logo_url: partner.logo_url,
      },
    })),
  );

  const firstPopulation = computed(() => selectedPopulations.value[0]);

  const populationName = computed(() => firstPopulation.value?.name ?? '');

  // Computed - Dynamic copy
  const modalTitle = computed(() => {
    const action = canEdit.value ? 'Edit' : 'View';
    if (props.isForPartner) {
      const partnerText = pluralize(
        props.selectedPartnerIds.value.length,
        'Partner',
      );
      return `${action} Sharing Settings for Selected ${partnerText} - ${populationName.value}`;
    }
    const populationText = pluralize(
      props.selectedPopulationIds.value.length,
      'Population',
    );
    const titleBase = `${action} Sharing Settings for Selected ${populationText}`;
    // Add population name if only one population is selected
    if (
      props.selectedPopulationIds.value.length === 1 &&
      populationName.value
    ) {
      return `${titleBase} - ${populationName.value}`;
    }
    return titleBase;
  });

  const shouldShowAlert = computed(() => {
    if (props.isForPartner) {
      return true;
    }
    return selectedPopulations.value.length > 1;
  });

  const alertMessage = computed(() => {
    if (props.isForPartner) {
      return "You're editing partner-specific sharing settings";
    }
    return "You're editing default sharing settings for multiple populations";
  });

  const alertDescription = computed(() => {
    if (props.isForPartner) {
      return `Changes made here apply only to the selected ${pluralize(
        props.selectedPartnerIds.value.length,
        'partner',
      )} and override the default sharing settings for the ${populationName.value} population.`;
    }
    return 'Changes will apply to all selected populations. Partner-specific overrides will remain intact.';
  });

  const sharingLevelDescription = computed(() => {
    if (props.isForPartner) {
      return `Changes made here apply only to the selected ${pluralize(
        props.selectedPartnerIds.value.length,
        'partner',
      )} and override the default sharing settings for the ${populationName.value} population.`;
    }
    const isSinglePopulation = props.selectedPopulationIds.value.length === 1;
    if (isSinglePopulation) {
      return "Choose how much of this population's data you want to share with partners.";
    }
    return "Choose how much of these populations' data you want to share with partners.";
  });

  const sharedFieldsTitle = computed(() => {
    if (props.isForPartner) {
      return 'Field Presets';
    }
    const populationText = pluralize(
      props.selectedPopulationIds.value.length,
      'Population',
    );
    return `Field Presets for Selected ${populationText}`;
  });

  const sharedFieldsDescription = computed(() => {
    if (props.isForPartner) {
      return `Changes made here apply only to the selected ${pluralize(
        props.selectedPartnerIds.value.length,
        'partner',
      )} and override the default sharing settings for the ${populationName.value} population.`;
    }
    const isSinglePopulation = props.selectedPopulationIds.value.length === 1;
    if (isSinglePopulation) {
      return 'Fields are configured per data source. Choose a field preset for the population.';
    }
    return 'Fields are configured per data source. Choose a field preset for each group of populations.';
  });

  const populationsByDataSource = computed(() => {
    const groups: Record<string, DataSourceGroup> = {};

    selectedPopulations.value.forEach((population) => {
      const feedId = getFeedId(population);
      if (!feedId) return;

      const isGoogleOrCsv = isGoogleOrCsvSource(population.integration_type);
      const sourceId = isGoogleOrCsv ? population.source_id : undefined;
      const key = `${feedId}_${sourceId ?? 'default'}`;

      if (!groups[key]) {
        groups[key] = {
          feedId,
          sourceId,
          populations: [],
          presetOptions: getPresetOptionsForFeed(feedId, sourceId),
          displayPresetId: null,
        };
      }

      groups[key].populations.push(population);
    });

    // For each group, determine the display preset ID
    Object.values(groups).forEach((group) => {
      if (props.isForPartner) {
        // For partner mode, get presets from partner settings
        const presetIds = props.selectedPartnerIds.value
          .map((partnerId) => partnerSettings.value[partnerId]?.presetId)
          .filter((id): id is number | null => id !== undefined);

        const allSame = presetIds.every((id) => id === presetIds[0]);

        // Only set displayPresetId if all partners have the same preset
        if (allSame && presetIds.length > 0) {
          group.displayPresetId = presetIds[0] ?? null;
        } else {
          // Multiple different presets - don't set any
          group.displayPresetId = null;
        }
      } else {
        // For population mode, get presets from population settings
        const presetIds = group.populations
          .map((pop) => populationSettings.value[pop.id]?.presetId)
          .filter((id): id is number | null => id !== undefined);

        const allSame = presetIds.every((id) => id === presetIds[0]);

        // Only set displayPresetId if all populations have the same preset
        if (allSame && presetIds.length > 0) {
          group.displayPresetId = presetIds[0] ?? null;
        } else {
          // Multiple different presets - don't set any
          group.displayPresetId = null;
        }
      }
    });

    return groups;
  });

  const hasItemsSelected = computed(() => {
    if (props.isForPartner) {
      return props.selectedPartnerIds.value.length > 0;
    }
    return props.selectedPopulationIds.value.length > 0;
  });

  const shouldShowPresetSelectors = computed(() => {
    // Hide preset selectors when no populations or partners are selected
    if (!hasItemsSelected.value) return false;

    // Show preset selectors when visibility is null (mixed), or when it requires presets
    return (
      globalVisibility.value === null ||
      (globalVisibility.value !== HIDDEN &&
        globalVisibility.value !== OLAPS_ONLY)
    );
  });

  const hasChanges = computed(() => {
    // Disable save if no items are selected
    const hasSelectedItems = props.isForPartner
      ? props.selectedPartnerIds.value.length > 0
      : props.selectedPopulationIds.value.length > 0;
    if (!hasSelectedItems) return false;

    // Disable save if no visibility is selected
    if (globalVisibility.value === null) return false;

    // If visibility requires presets, check that all presets are selected
    const requiresPresets =
      globalVisibility.value !== HIDDEN &&
      globalVisibility.value !== OLAPS_ONLY;

    if (requiresPresets) {
      // Check if any preset is null
      if (props.isForPartner) {
        const hasNullPreset = props.selectedPartnerIds.value.some((id) => {
          const settings = partnerSettings.value[id];
          return settings?.presetId === null;
        });
        if (hasNullPreset) return false;
      } else {
        const hasNullPreset = props.selectedPopulationIds.value.some((id) => {
          const settings = populationSettings.value[id];
          return settings?.presetId === null;
        });
        if (hasNullPreset) return false;
      }
    }

    // For new populations, always allow saving once validation passes
    if (props.isNewPopulation) return true;

    // Check if global visibility changed
    if (globalVisibility.value !== initialGlobalVisibility.value) return true;

    if (props.isForPartner) {
      // Check if any partner preset changed
      return props.selectedPartnerIds.value.some((id) => {
        const current = partnerSettings.value[id];
        const initial = initialSettings.value[id];
        if (!current || !initial) return false;
        return current.presetId !== initial.presetId;
      });
    }

    // Check if any population preset changed
    return props.selectedPopulationIds.value.some((id) => {
      const current = populationSettings.value[id];
      const initial = initialSettings.value[id];
      if (!current || !initial) return false;
      return current.presetId !== initial.presetId;
    });
  });

  // Helper functions
  function getFeedId(population: Population): number {
    return (
      getFeedIdForPopulationUtil(population, (id) =>
        sourcesStore.getSourceById(id),
      ) ?? population.feed_id
    );
  }

  function getPresetOptionsForFeed(
    feedId: number,
    sourceId: number | undefined,
  ): PresetOptionsGroup[] {
    const presets = dataSharePresetsStore.getFilteredPresetsByFeeId(
      feedId,
      sourceId,
    );
    return dataSharePresetsStore.convertPresetsToOptions(
      presets,
      feedId,
      sourceId,
    );
  }

  function handleDataSourcePresetChange(
    populations: Population[],
    presetId: number | null,
  ) {
    if (props.isForPartner) {
      // Apply the preset to all selected partners
      const updatedSettings = { ...partnerSettings.value };
      props.selectedPartnerIds.value.forEach((partnerId) => {
        const currentSetting = updatedSettings[partnerId];
        if (currentSetting) {
          updatedSettings[partnerId] = {
            visibility: currentSetting.visibility,
            presetId,
          };
        }
      });
      partnerSettings.value = updatedSettings;
    } else {
      // Apply the preset to all populations in this data source group
      const updatedSettings = { ...populationSettings.value };
      populations.forEach((population) => {
        const currentSetting = updatedSettings[population.id];
        if (currentSetting) {
          updatedSettings[population.id] = {
            visibility: currentSetting.visibility,
            presetId,
          };
        }
      });
      populationSettings.value = updatedSettings;
    }
  }

  function handleOpenPresetModal(
    feedId: number,
    sourceId: number | undefined,
    presetId: number | undefined,
  ) {
    openModal({
      feedId,
      sourceId,
      presetId,
      isForPartner: props.isForPartner,
      onSuccess: (_newPresetId: number, _isNewPreset: boolean) => {
        dataSharePresetsStore.refreshDataSharePresetsStore();
      },
    });
  }

  function getVisiblePopulations(group: DataSourceGroup, groupKey: string) {
    const visibleCount = visiblePopulationCounts[groupKey] || 3;
    return group.populations.slice(0, visibleCount);
  }

  function getRemainingCount(group: DataSourceGroup, groupKey: string) {
    const visibleCount = visiblePopulationCounts[groupKey] || 3;
    return Math.max(0, group.populations.length - visibleCount);
  }

  function isFullyExpanded(group: DataSourceGroup, groupKey: string) {
    const visibleCount = visiblePopulationCounts[groupKey] || 3;
    return visibleCount >= group.populations.length;
  }

  function shouldShowExpandButton(group: DataSourceGroup) {
    return group.populations.length > 3;
  }

  function expandPopulationsForGroup(groupKey: string) {
    const currentCount = visiblePopulationCounts[groupKey] || 3;
    visiblePopulationCounts[groupKey] = currentCount + 3;
  }

  function collapsePopulationsForGroup(groupKey: string) {
    visiblePopulationCounts[groupKey] = 3;
  }

  function removeSettingsForIds(
    settingsRef: Ref<Record<number, SharingSetting>>,
    ids: number[],
  ) {
    const removedSet = new Set(ids);
    settingsRef.value = Object.fromEntries(
      Object.entries(settingsRef.value).filter(
        ([key]) => !removedSet.has(Number(key)),
      ),
    );
    initialSettings.value = Object.fromEntries(
      Object.entries(initialSettings.value).filter(
        ([key]) => !removedSet.has(Number(key)),
      ),
    );
  }

  function recomputeGlobalVisibility(
    ids: number[],
    settings: Record<number, SharingSetting>,
    defaultVisibility: SharingType,
  ) {
    const visibilities = ids
      .map((id) => settings[id]?.visibility)
      .filter((v): v is SharingType => v !== undefined);

    const allSame = visibilities.every((v) => v === visibilities[0]);
    globalVisibility.value =
      allSame && visibilities.length > 0
        ? (visibilities[0] ?? defaultVisibility)
        : null;
  }

  function recomputeInitialGlobalVisibility(
    ids: number[],
    defaultVisibility: SharingType,
  ) {
    const visibilities = ids
      .map((id) => initialSettings.value[id]?.visibility)
      .filter((v): v is SharingType => v !== undefined);

    const allSame = visibilities.every((v) => v === visibilities[0]);
    initialGlobalVisibility.value =
      allSame && visibilities.length > 0
        ? (visibilities[0] ?? defaultVisibility)
        : null;
  }

  async function handleSave() {
    isSaving.value = true;

    // Early return if visibility is null (this should be prevented by hasChanges)
    if (globalVisibility.value === null) {
      isSaving.value = false;
      return;
    }

    // Early return if no items are selected
    const hasSelectedItems = props.isForPartner
      ? props.selectedPartnerIds.value.length > 0
      : props.selectedPopulationIds.value.length > 0;
    if (!hasSelectedItems) {
      isSaving.value = false;
      return;
    }

    const visibility = globalVisibility.value;

    if (props.isForPartner) {
      // Partner mode: use different API endpoint
      const populationId = props.selectedPopulationIds.value[0];
      if (!populationId) {
        flashesStore.addUnhandledError(
          new Error('No population ID provided for partner sharing'),
        );
        isSaving.value = false;
        return;
      }

      const dataShares = props.selectedPartnerIds.value
        .map((partnerId) => {
          const settings = partnerSettings.value[partnerId];
          if (!settings) return null;

          const presetId =
            visibility === HIDDEN || visibility === OLAPS_ONLY
              ? undefined
              : (settings.presetId ?? undefined);

          return {
            partner_organization_id: partnerId,
            visibility,
            preset_id: presetId,
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      const payload = {
        data_shares: dataShares,
      };

      const { error } = await crossbeamApi.POST(
        '/v0.1/data-share-assignments/populations/{population_id}',
        {
          params: {
            path: {
              population_id: populationId,
            },
          },
          body: payload,
        },
      );

      if (error) {
        flashesStore.addUnhandledError(
          new Error('Failed to save sharing settings'),
        );
        isSaving.value = false;
        return;
      }
    } else {
      // Population mode: use populations endpoint
      const dataShares = props.selectedPopulationIds.value
        .map((id) => {
          const settings = populationSettings.value[id];
          if (!settings) return null;

          const presetId =
            visibility === HIDDEN || visibility === OLAPS_ONLY
              ? undefined
              : (settings.presetId ?? undefined);

          return {
            population_id: id,
            visibility,
            preset_id: presetId,
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      const payload = {
        data_shares: dataShares,
      };

      const { error } = await crossbeamApi.POST(
        '/v0.1/data-share-assignments/populations',
        {
          body: payload,
        },
      );

      if (error) {
        flashesStore.addUnhandledError(
          new Error('Failed to save sharing settings'),
        );
        isSaving.value = false;
        return;
      }
    }

    await dataSharesStore.refreshDataSharesStore();

    // Track changes for analytics (before updating initial state)
    const isBulkPopulation =
      !props.isForPartner && props.selectedPopulationIds.value.length > 1;
    const isBulkPartner =
      props.isForPartner && props.selectedPartnerIds.value.length > 1;

    if (props.isForPartner) {
      const savedVisibility = visibility;
      const popId = props.selectedPopulationIds.value[0];
      if (popId !== undefined) {
        const population = populationsStore.getPopulationById(popId);
        props.selectedPartnerIds.value.forEach((partnerId) => {
          const current = partnerSettings.value[partnerId];
          const initial = initialSettings.value[partnerId] as
            | SharingSetting
            | undefined;
          if (!current) return;

          trackDataSharingChangeFromContext({
            population: population ?? undefined,
            current: {
              visibility: savedVisibility,
              presetId: current.presetId,
            },
            previous: initial
              ? { visibility: initial.visibility, presetId: initial.presetId }
              : undefined,
            eventSite: EVENT_SITES.POPULATION_SHARING_MODAL,
            bulk: isBulkPartner,
            partnerId,
          });
        });
      }
    } else {
      const savedVisibility = visibility;
      props.selectedPopulationIds.value.forEach((id) => {
        const current = populationSettings.value[id];
        const initial = initialSettings.value[id] as SharingSetting | undefined;
        if (!current) return;

        const population = populationsStore.getPopulationById(id);
        trackDataSharingChangeFromContext({
          population: population ?? undefined,
          current: {
            visibility: savedVisibility,
            presetId: current.presetId,
          },
          previous: initial
            ? { visibility: initial.visibility, presetId: initial.presetId }
            : undefined,
          eventSite: EVENT_SITES.POPULATION_SHARING_MODAL,
          bulk: isBulkPopulation,
        });
      });
    }

    // Update initial settings to match current settings
    initialGlobalVisibility.value = globalVisibility.value;

    if (props.isForPartner) {
      props.selectedPartnerIds.value.forEach((id) => {
        if (partnerSettings.value[id]) {
          initialSettings.value[id] = { ...partnerSettings.value[id] };
        }
      });
    } else {
      props.selectedPopulationIds.value.forEach((id) => {
        if (populationSettings.value[id]) {
          initialSettings.value[id] = { ...populationSettings.value[id] };
        }
      });
    }

    // Show success message
    flashesStore.addSuccessFlash({ message: 'Settings saved' });

    isSaving.value = false;
    emit('saved');
    emit('close');
  }

  function initializeSettings() {
    if (props.isForPartner) {
      // Initialize partner settings
      const populationId = props.selectedPopulationIds.value[0];
      if (!populationId) return;

      const sharingRules = dataSharesStore.getOutgoingSharingRules({
        ruleType: PARTNER_RULE,
        populationId,
      });

      const populationDefaultRule = dataSharesStore.getOutgoingSharingRules({
        ruleType: POPULATION_RULE,
        populationId,
      })[0];

      const defaultVisibility =
        (populationDefaultRule?.visibility as SharingType) || HIDDEN;
      const defaultPresetId =
        populationDefaultRule?.data_share_presets_id || null;

      const newPartnerSettings: Record<number, SharingSetting> = {};
      const visibilities: SharingType[] = [];

      props.selectedPartnerIds.value.forEach((partnerId) => {
        const currentRule = sharingRules.find(
          (rule) => rule.partner_org_id === partnerId,
        );

        const visibility =
          (currentRule?.visibility as SharingType) || defaultVisibility;
        const presetId = currentRule?.data_share_presets_id ?? defaultPresetId;

        newPartnerSettings[partnerId] = {
          visibility,
          presetId,
        };

        initialSettings.value[partnerId] = {
          visibility,
          presetId,
        };

        visibilities.push(visibility);
      });

      partnerSettings.value = newPartnerSettings;
      recomputeGlobalVisibility(
        props.selectedPartnerIds.value,
        newPartnerSettings,
        HIDDEN,
      );
      initialGlobalVisibility.value = globalVisibility.value;
    } else {
      // Initialize population settings
      const sharingRules = dataSharesStore.getOutgoingSharingRules({
        ruleType: POPULATION_RULE,
      });

      const newPopulationSettings: Record<number, SharingSetting> = {};
      const visibilities: SharingType[] = [];

      props.selectedPopulationIds.value.forEach((id) => {
        const population = populationsStore.getPopulationById(id);
        if (!population) return;

        const currentRule = sharingRules.find(
          (rule) => rule.population_id === id,
        );

        // For new populations (no rule), default to SHARING (Overlapping Accounts) instead of HIDDEN
        const visibility = (currentRule?.visibility as SharingType) || SHARING;
        const presetId = currentRule?.data_share_presets_id || null;

        newPopulationSettings[id] = {
          visibility,
          presetId,
        };

        initialSettings.value[id] = {
          visibility,
          presetId,
        };

        visibilities.push(visibility);
      });

      populationSettings.value = newPopulationSettings;
      recomputeGlobalVisibility(
        props.selectedPopulationIds.value,
        newPopulationSettings,
        SHARING,
      );
      initialGlobalVisibility.value = globalVisibility.value;
    }
  }

  async function initialize() {
    await Promise.all([
      populationsStore.readySync,
      partnersStore.readySync,
      dataSharesStore.readySync,
      dataSharePresetsStore.readySync,
    ]);
    initializeSettings();
  }

  // Watch for globalVisibility changes to propagate to individual settings
  // and set Crossbeam default presets when switching from HIDDEN/OLAPS_ONLY
  watch(globalVisibility, (newVisibility, oldVisibility) => {
    // Propagate visibility to individual settings so they stay in sync
    if (newVisibility !== null) {
      if (props.isForPartner) {
        props.selectedPartnerIds.value.forEach((id) => {
          const settings = partnerSettings.value[id];
          if (settings) {
            settings.visibility = newVisibility;
          }
        });
      } else {
        props.selectedPopulationIds.value.forEach((id) => {
          const settings = populationSettings.value[id];
          if (settings) {
            settings.visibility = newVisibility;
          }
        });
      }
    }

    const wasHiddenOrOlapsOnly =
      oldVisibility === null ||
      oldVisibility === HIDDEN ||
      oldVisibility === OLAPS_ONLY;
    const nowRequiresPresets =
      newVisibility !== null &&
      newVisibility !== HIDDEN &&
      newVisibility !== OLAPS_ONLY;

    if (wasHiddenOrOlapsOnly && nowRequiresPresets) {
      // For each data source group, set Crossbeam default if preset is null
      Object.values(populationsByDataSource.value).forEach((group) => {
        if (props.isForPartner) {
          // Update partner settings if they have null preset
          props.selectedPartnerIds.value.forEach((partnerId) => {
            const settings = partnerSettings.value[partnerId];
            if (settings && settings.presetId === null) {
              let defaultPresetId =
                dataSharePresetsStore.getDefaultPresetId(
                  group.feedId,
                  group.sourceId,
                ) ?? null;

              // If no default preset, find the Crossbeam default preset
              if (defaultPresetId === null) {
                const crossbeamDefaultPreset = group.presetOptions
                  .flatMap((optGroup) => optGroup.items)
                  .find((item) => item.presetType === 'crossbeam_default');
                defaultPresetId =
                  typeof crossbeamDefaultPreset?.value === 'number'
                    ? crossbeamDefaultPreset.value
                    : null;
              }

              if (defaultPresetId !== null) {
                settings.presetId = defaultPresetId;
              }
            }
          });
        } else {
          // Update population settings if they have null preset
          group.populations.forEach((pop) => {
            const settings = populationSettings.value[pop.id];
            if (settings && settings.presetId === null) {
              let defaultPresetId =
                dataSharePresetsStore.getDefaultPresetId(
                  group.feedId,
                  group.sourceId,
                ) ?? null;

              // If no default preset, find the Crossbeam default preset
              if (defaultPresetId === null) {
                const crossbeamDefaultPreset = group.presetOptions
                  .flatMap((optGroup) => optGroup.items)
                  .find((item) => item.presetType === 'crossbeam_default');
                defaultPresetId =
                  typeof crossbeamDefaultPreset?.value === 'number'
                    ? crossbeamDefaultPreset.value
                    : null;
              }

              if (defaultPresetId !== null) {
                settings.presetId = defaultPresetId;
              }
            }
          });
        }
      });
    }
  });

  // These watchers ensure that adding a new population/partner in multi-selects
  // correctly initializes sharing settings.
  watch(
    () => props.selectedPopulationIds.value,
    (newIds, oldIds) => {
      if (props.isForPartner) return;

      const oldSet = new Set(oldIds);
      const newSet = new Set(newIds);

      // Remove settings for populations that were deselected
      const removedIds = oldIds.filter((id) => !newSet.has(id));
      if (removedIds.length) {
        removeSettingsForIds(populationSettings, removedIds);
      }

      // Add settings for newly selected populations
      const addedIds = newIds.filter((id) => !oldSet.has(id));
      if (addedIds.length) {
        const sharingRules = dataSharesStore.getOutgoingSharingRules({
          ruleType: POPULATION_RULE,
        });

        const updated = { ...populationSettings.value };
        addedIds.forEach((id) => {
          const population = populationsStore.getPopulationById(id);
          if (!population) return;

          const currentRule = sharingRules.find(
            (rule) => rule.population_id === id,
          );

          const visibility =
            (currentRule?.visibility as SharingType) || SHARING;
          const presetId = currentRule?.data_share_presets_id || null;

          updated[id] = { visibility, presetId };
          initialSettings.value[id] = { visibility, presetId };
        });
        populationSettings.value = updated;
      }

      // Recompute global visibility
      if (removedIds.length || addedIds.length) {
        recomputeGlobalVisibility(newIds, populationSettings.value, SHARING);
        recomputeInitialGlobalVisibility(newIds, SHARING);
      }
    },
  );

  // Watch for selectedPartnerIds changes to incrementally update settings
  // (e.g. when user adds/removes partners via the multi-select in the modal)
  watch(
    () => props.selectedPartnerIds.value,
    (newIds, oldIds) => {
      if (!props.isForPartner) return;

      const oldSet = new Set(oldIds);
      const newSet = new Set(newIds);

      // Remove settings for partners that were deselected
      const removedIds = oldIds.filter((id) => !newSet.has(id));
      if (removedIds.length) {
        removeSettingsForIds(partnerSettings, removedIds);
      }

      // Add settings for newly selected partners
      const addedIds = newIds.filter((id) => !oldSet.has(id));
      if (addedIds.length) {
        const populationId = props.selectedPopulationIds.value[0];
        if (!populationId) return;

        const sharingRules = dataSharesStore.getOutgoingSharingRules({
          ruleType: PARTNER_RULE,
          populationId,
        });

        const populationDefaultRule = dataSharesStore.getOutgoingSharingRules({
          ruleType: POPULATION_RULE,
          populationId,
        })[0];

        const defaultVisibility =
          (populationDefaultRule?.visibility as SharingType) || HIDDEN;
        const defaultPresetId =
          populationDefaultRule?.data_share_presets_id || null;

        const updated = { ...partnerSettings.value };
        addedIds.forEach((partnerId) => {
          const currentRule = sharingRules.find(
            (rule) => rule.partner_org_id === partnerId,
          );

          const visibility =
            (currentRule?.visibility as SharingType) || defaultVisibility;
          const presetId =
            currentRule?.data_share_presets_id ?? defaultPresetId;

          updated[partnerId] = { visibility, presetId };
          initialSettings.value[partnerId] = { visibility, presetId };
        });
        partnerSettings.value = updated;
      }

      // Recompute global visibility
      if (removedIds.length || addedIds.length) {
        recomputeGlobalVisibility(newIds, partnerSettings.value, HIDDEN);
        recomputeInitialGlobalVisibility(newIds, HIDDEN);
      }
    },
  );

  return {
    // State
    globalVisibility,
    isSaving,
    visiblePopulationCounts,

    // Computed
    modalTitle,
    shouldShowAlert,
    alertMessage,
    alertDescription,
    sharingLevelDescription,
    sharedFieldsTitle,
    sharedFieldsDescription,
    populationsByDataSource,
    shouldShowPresetSelectors,
    hasChanges,
    hasItemsSelected,
    canEdit,
    isGreenfieldSharingBlocked,
    allPopulationOptions,
    allPartnerOptions,

    // Functions
    handleDataSourcePresetChange,
    handleOpenPresetModal,
    getVisiblePopulations,
    getRemainingCount,
    isFullyExpanded,
    shouldShowExpandButton,
    expandPopulationsForGroup,
    collapsePopulationsForGroup,
    handleSave,
    initialize,
  };
}
